import { z } from 'zod';
import { ServiceStatus } from '../generated/prisma/enums.ts';
import { idSchema, localeSchema } from './content-primitives.ts';
import type { ContentObject } from './content.ts';
import { assetFileSchema, recordSchemas, renderContentKinds, siteSettingsSchema } from './content.ts';
import { customDomainSummarySchema, domainSummarySchema } from './domains.ts';
import { validationIssueSchema } from './errors.ts';
import { submissionActionSchema } from './form-actions.ts';

export const siteSchema = z.strictObject({
  id: z.string(),
  ownerUserId: z.string(),
  createdAt: z.iso.datetime().meta({ pattern: undefined }),
  archivedAt: z.iso.datetime().meta({ pattern: undefined }).nullable(),
  billingStatus: z.enum(ServiceStatus),
  domain: domainSummarySchema,
  pendingDomain: customDomainSummarySchema.nullable(),
});

const readOnly = <T extends z.ZodType>(schema: T, description?: string) =>
  schema.meta({ readOnly: true, ...(description ? { description } : {}) });
const envelope = {
  id: readOnly(idSchema, 'Stable object ID shared by all saved versions.'),
  versionId: readOnly(idSchema, 'Immutable ID of this saved version.'),
  createdAt: readOnly(z.iso.datetime().meta({ pattern: undefined })),
  isPublished: readOnly(z.boolean(), 'Whether this exact version is currently live.'),
};
export const recordObjectSchemas = {
  Settings: siteSettingsSchema.safeExtend({ ...envelope, kind: z.literal('Settings') }),
  Page: recordSchemas.Page.safeExtend({ ...envelope, kind: z.literal('Page') }),
  Post: recordSchemas.Post.safeExtend({
    ...envelope,
    kind: z.literal('Post'),
    publishedAt: recordSchemas.Post.shape.publishedAt.meta({ pattern: undefined }),
  }),
  Form: recordSchemas.Form.safeExtend({ ...envelope, kind: z.literal('Form') }),
  FormSubmissionAction: submissionActionSchema,
  Asset: recordSchemas.Asset.safeExtend({
    ...envelope,
    kind: z.literal('Asset'),
    fileName: readOnly(assetFileSchema.shape.fileName),
    mimeType: readOnly(assetFileSchema.shape.mimeType),
    byteSize: readOnly(assetFileSchema.shape.byteSize),
    width: readOnly(assetFileSchema.shape.width),
    height: readOnly(assetFileSchema.shape.height),
    url: readOnly(assetFileSchema.shape.url, 'The public URL of the file.'),
  }),
};
export const contentObjectSchema: z.ZodType<ContentObject> = z.union([
  recordObjectSchemas.Settings,
  recordObjectSchemas.Page,
  recordObjectSchemas.Post,
  recordObjectSchemas.Form,
  recordObjectSchemas.Asset,
  recordObjectSchemas.FormSubmissionAction,
]);
const renderContentObjectSchema = z.union(renderContentKinds.map((kind) => recordObjectSchemas[kind]));

export const snapshotSchema = z.object({
  siteId: idSchema,
  objects: z.array(renderContentObjectSchema),
});
export const contentVersionSchema = z.enum(['Draft', 'Published']).default('Draft');
export const publicationSchema = z.object({
  id: idSchema,
  siteId: idSchema,
  publishedAt: z.iso.datetime().meta({ pattern: undefined }),
  actorType: z.enum(['User', 'BlogAutomation']),
  publishedById: idSchema.nullable(),
  siteSettingsVersionId: idSchema,
  pageVersionIds: z.array(idSchema),
  formVersionIds: z.array(idSchema),
  formSubmissionActionVersionIds: z.array(idSchema),
  assetVersionIds: z.array(idSchema),
});
export const publicationResultSchema = z.object({
  publicationId: idSchema,
  publishedAt: z.iso.datetime().meta({ pattern: undefined }),
});
export const unpublishedChangeSchema = z
  .object({
    objectId: idSchema,
    kind: z.string(),
    change: z.enum(['Added', 'Modified', 'Archived']),
    published: contentObjectSchema.nullable(),
    draft: contentObjectSchema,
  })
  .describe(
    'A content difference between the shared draft and live version. Pending archival is included; equal content is omitted even when version IDs differ.',
  );
export const accountSummarySchema = z.object({
  id: idSchema,
  owner: z.boolean(),
  ownerName: z.string(),
  ownerEmail: z.string(),
  paymentMethodAccess: z.boolean(),
  stripeSubscriptionStatus: z.string().nullable(),
});

export function ownerLabel(person: { name: string; email: string }) {
  return `${person.name} · ${person.email}`;
}
export const siteSummarySchema = z.object({
  id: idSchema,
  ownerUserId: idSchema,
  name: z.string(),
  baseLocale: localeSchema,
  published: z.boolean(),
  unpublishedChangeCount: z.number().int().nonnegative(),
  lastPublishedAt: z.iso.datetime().meta({ pattern: undefined }).nullable(),
  domain: z.string().nullable(),
  favicon: z.url().nullable(),
});
export const validationSchema = z.object({
  valid: z.boolean(),
  errors: z.array(validationIssueSchema),
});
